import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { resolveUrl } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const HERO = "https://customer-assets-jai6qajn.emergentagent.net/job_club-inventory-hub/artifacts/prno0hnj_image.png";

export default function Login() {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("auto");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [brand, setBrand] = useState({ club_name: "ClubTeesta", logo_url: "" });

  useEffect(() => { api.get("/branding").then((r) => setBrand(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (user) navigate("/portal", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(userId, password, role === "auto" ? undefined : role);
      toast.success(`Welcome back, ${u.name}`);
      navigate("/portal", { replace: true });
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-brand-bg" data-testid="login-page">
      {/* Cinematic panel */}
      <div className="relative lg:w-1/2 min-h-[220px] lg:min-h-screen overflow-hidden">
        <img src={HERO} alt="Clubhouse" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-brand-dark/55" />
        <div className="relative z-10 h-full flex flex-col justify-between p-8 lg:p-14">
          <div className="flex items-center gap-3">
            {brand.logo_url
              ? <img src={resolveUrl(brand.logo_url)} alt="logo" className="w-11 h-11 rounded-sm object-cover bg-white" data-testid="login-logo" />
              : <div className="w-9 h-9 rounded-sm bg-brand-gold flex items-center justify-center font-serif text-brand-dark text-xl font-bold">C</div>}
            <span className="text-brand-bg font-serif text-2xl tracking-tight">{brand.club_name}</span>
          </div>
          <div className="hidden lg:block">
            <p className="text-xs uppercase tracking-[0.25em] text-brand-gold mb-4">Members · Culinary · Concierge</p>
            <p className="text-brand-bg/70 mt-6 max-w-sm leading-relaxed">
              One portal for members, kitchen staff, and management — orders and live inventory in perfect service.
            </p>
          </div>
        </div>
      </div>

      {/* Form panel */}
      <div className="lg:w-1/2 flex items-center justify-center p-8 lg:p-14">
        <div className="w-full max-w-sm animate-fade-up">
          <p className="text-xs uppercase tracking-[0.25em] text-brand-gold mb-3">Teesta Club Portal</p>
          <h2 className="font-serif text-4xl tracking-tighter text-brand-text mb-2">Sign in</h2>
          <p className="text-brand-muted text-sm mb-8">Access your account.</p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-brand-text">Account Type</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="login-role-select" className="bg-white border-brand-line h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto-detect (recommended)</SelectItem>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="userid" className="text-sm font-semibold text-brand-text">User ID</Label>
              <Input id="userid" type="text" autoCapitalize="none" autoCorrect="off" required value={userId} onChange={(e) => setUserId(e.target.value)}
                placeholder="name.surname" data-testid="login-username-input"
                className="bg-white border-brand-line focus-visible:ring-brand-primary h-11" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-semibold text-brand-text">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" data-testid="login-password-input"
                className="bg-white border-brand-line focus-visible:ring-brand-primary h-11" />
            </div>

            {error && <p className="text-sm text-brand-alert" data-testid="login-error">{error}</p>}

            <Button type="submit" disabled={loading} data-testid="login-submit-button"
              className="w-full h-11 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm group">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <span className="flex items-center gap-2">Enter Portal <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></span>
              )}
            </Button>
          </form>

          <p className="text-xs text-brand-muted text-center mt-8 leading-relaxed" data-testid="login-footer-credit">
            © 2026 Teesta Club. All Rights Reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
